import React from "react";
import { Formik, FormikProps, FormikActions } from "formik";
import * as Yup from "yup";
import { Values } from "Tools/Values";
import { STFormWrapper } from "@src/Components/Ui/STFormWrapper";
import { inject, observer } from "mobx-react";
import { IValuesStore } from "@src/Stores/Values/types";
import { IManagementStore } from "@src/Stores/Management/types";
import PasswordField from "./PasswordField/PasswordField";
import {
  STTitle,
  STPasswordRequirementsWrapper,
  STPasswordRequirementsTitle,
  STPasswordRequirementsText,
  STCheckCircleIcon,
  STErrorIcon,
  STUserTitle,
} from "./styles";
import { Grid } from "@material-ui/core";

const passwordSchema = Yup.object().shape({
  password: Yup.string().required(() => {
    return Values.getLocalizationValue("fieldRequired");
  }),
  repeated: Yup.string()
    .oneOf([Yup.ref("password"), null], () => Values.getLocalizationValue("dontMatch"))
    .required(() => Values.getLocalizationValue("fieldRequired")),
});

interface IProps {
  onSubmit: (values: IPasswordForm, formikActions: FormikActions<IPasswordForm>) => void;
  values?: IValuesStore;
  management?: IManagementStore;
  title: string;
  personName?: string;
}

export interface IPasswordForm {
  password: string;
  repeated: string;
}

@inject("values", "management")
@observer
class ChangePasswordForm extends React.Component<IProps> {
  private arePasswordRequirementsMet = {
    minPasswordLength: false,
    needUpperCaseLowerCase: false,
    needSpecialChars: false,
  };

  async componentDidMount() {
    await this.props.management.getPasswordOptions();
    this.forceUpdate();
  }

  handlePasswordChange = (event: any, formikProps: FormikProps<IPasswordForm>) => {
    const { minPasswordLength, needUpperCaseLowerCase, needSpecialChars } = this.props.management.passwordOptions || {};
    const password = event.target.value;
    this.arePasswordRequirementsMet.minPasswordLength = password.length >= minPasswordLength;
    if (needUpperCaseLowerCase) {
      this.arePasswordRequirementsMet.needUpperCaseLowerCase = /^(?=.*[a-z])(?=.*[A-Z])/.test(password);
    }
    if (needSpecialChars) {
      this.arePasswordRequirementsMet.needSpecialChars = /^(?=.*[!@#\$%\^&\*])/.test(password);
    }
    formikProps.setFieldValue("password", password);
    this.forceUpdate();
  };

  handleRepeatePasswordChange = (event: any, formikProps: FormikProps<IPasswordForm>) => {
    formikProps.setFieldValue("repeated", event.target.value);
    this.forceUpdate();
  };

  handleSubmit = (values: IPasswordForm, formikActions: FormikActions<IPasswordForm>) => {
    const { minPasswordLength, needUpperCaseLowerCase, needSpecialChars } = this.props.management.passwordOptions || {};
    if (
      (minPasswordLength && !this.arePasswordRequirementsMet.minPasswordLength) ||
      (needUpperCaseLowerCase && !this.arePasswordRequirementsMet.needUpperCaseLowerCase) ||
      (needSpecialChars && !this.arePasswordRequirementsMet.needSpecialChars)
    ) {
      return;
    }
    this.props.onSubmit(values, formikActions);
  };

  render() {
    const { minPasswordLength, needUpperCaseLowerCase, needSpecialChars } = this.props.management.passwordOptions || {};
    return (
      <Formik
        initialValues={{ password: "", repeated: "" }}
        validationSchema={passwordSchema}
        onSubmit={(values: IPasswordForm, formikActions: FormikActions<IPasswordForm>) =>
          this.handleSubmit(values, formikActions)
        }
        render={(formikProps: FormikProps<IPasswordForm>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <Grid container justify="flex-start" style={{ paddingBottom: "20px" }}>
              <Grid item xs={this.props.personName ? 7 : 12}>
                <STTitle>{this.props.title}</STTitle>
              </Grid>
              {this.props.personName && (
                <Grid item xs={5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <STUserTitle variant="body2">{this.props.personName}</STUserTitle>
                </Grid>
              )}
            </Grid>

            <PasswordField
              name="password"
              onChange={this.handlePasswordChange}
              labelKey={Values.getLocalizationValue("newPassword")}
              formikProps={formikProps}
            />
            <PasswordField
              name="repeated"
              onChange={this.handleRepeatePasswordChange}
              labelKey={Values.getLocalizationValue("repeatNewPassword")}
              formikProps={formikProps}
            />

            <STPasswordRequirementsWrapper container>
              <Grid item xs={12}>
                <STPasswordRequirementsTitle variant="body2">{`${Values.getLocalizationValue(
                  "passwordRequirements"
                )}:`}</STPasswordRequirementsTitle>
              </Grid>
              <Grid item xs={12} style={{ display: "flex" }}>
                {this.arePasswordRequirementsMet.minPasswordLength ? <STCheckCircleIcon /> : <STErrorIcon />}
                <STPasswordRequirementsText variant="body2">{`${Values.getLocalizationValue(
                  "minPasswordLength"
                )}: ${minPasswordLength}`}</STPasswordRequirementsText>
              </Grid>
              {needUpperCaseLowerCase && (
                <Grid item xs={12} style={{ display: "flex" }}>
                  {this.arePasswordRequirementsMet.needUpperCaseLowerCase ? <STCheckCircleIcon /> : <STErrorIcon />}
                  <STPasswordRequirementsText variant="body2">
                    {Values.getLocalizationValue("passwordNeedsUpperCaseLowerCaseLetters")}
                  </STPasswordRequirementsText>
                </Grid>
              )}
              {needSpecialChars && (
                <Grid item xs={12} style={{ display: "flex" }}>
                  {this.arePasswordRequirementsMet.needSpecialChars ? <STCheckCircleIcon /> : <STErrorIcon />}
                  <STPasswordRequirementsText variant="body2">
                    {Values.getLocalizationValue("passwordNeedsSpecialChars")}
                  </STPasswordRequirementsText>
                </Grid>
              )}
            </STPasswordRequirementsWrapper>

            {this.props.children}
          </STFormWrapper>
        )}
      />
    );
  }
}

export default ChangePasswordForm;
